import {
    AppBar,
    Toolbar,
    IconButton,
    useTheme,
    Button,
    Menu,
    MenuItem,
    Box,
    useMediaQuery,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Collapse
} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import {RootState} from '../store';
import {toggleTheme} from '../store/themeSlice';

import {useState} from 'react';
import {getImage} from '../utils/imageImports';
import {useLocation} from 'react-router-dom';
import {API_BASE_URL} from '../services/api';

interface NavLink {
    title: string;
    name?: string;
    href?: string;
    to?: string;
    subNavLinks?: NavLink[];
}

export default function AppHeader() {
    const {t, i18n} = useTranslation();
    const location = useLocation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const darkMode = useSelector((state: RootState) => state.theme.darkMode);
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));

    const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
    const [featuresAnchorEl, setFeaturesAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setLanguageAnchorEl(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setLanguageAnchorEl(null);
    };

    const handleFeaturesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setFeaturesAnchorEl(event.currentTarget);
    };

    const handleFeaturesMenuClose = () => {
        setFeaturesAnchorEl(null);
    };

    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        handleLanguageMenuClose();
    };

    const navLinks: NavLink[] = [
        {title: 'navigation.home', name: 'home', to: '/'},
        {
            title: 'navigation.features',
            subNavLinks: [
                {
                    title: 'navigation.features-list.email-testing',
                    name: 'email-design-testing',
                    to: '/features/email-design-testing'
                },
                {
                    title: 'navigation.features-list.email-analytics',
                    name: 'email-analytic',
                    to: '/features/email-analytic'
                },
                {
                    title: 'navigation.features-list.spam-testing',
                    name: 'inbox-spam-testing',
                    to: '/features/inbox-spam-testing'
                },
                {
                    title: 'navigation.features-list.white-label-api',
                    name: 'white-label-api',
                    to: '/features/white-label-api'
                },
                // {title: 'navigation.features-list.email-editor', name: 'email-editor', to: '/features/email-editor'},
            ],
        },
        {title: 'navigation.pricing', name: 'pricing', to: '/pricing'},
        {title: 'navigation.blog', name: 'blog', to: '/blog'},
        {title: 'navigation.about', name: 'about', to: '/about'},
        {title: 'navigation.contacts', name: 'contacts', to: '/contact'},
    ];

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                bgcolor: '#2a3447',
                '& .MuiButton-root': {
                    color: '#fff'
                },
                '& .MuiIconButton-root': {
                    color: '#fff'
                },
                '& .MuiSvgIcon-root': {
                    color: '#fff'
                }
            }}
        >
            <Toolbar
                sx={{
                    justifyContent: 'space-between',
                    pl: {xs: 3, sm: 4, md: 5},
                    pr: {xs: 1.5, sm: 2, md: 2.5},
                    minHeight: '64px'
                }}>
                {!isLargeScreen && (
                    <>
                        <IconButton
                            edge="start"
                            sx={{color: '#fff'}}
                            aria-label="menu"
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <MenuIcon/>
                        </IconButton>
                        <Drawer
                            anchor="left"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: 280,
                                    color: 'text.secondary'
                                }
                            }}
                        >
                            <List sx={{pt: 2}}>
                                {navLinks.map((link) => (
                                    link.subNavLinks ? (
                                        <Box key={link.title}>
                                            <ListItem
                                                component="div"
                                                onClick={handleFeaturesMenuOpen}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255, 138, 101, 0.04)',
                                                        '& .MuiListItemText-root': {color: '#FF8A65'}
                                                    },
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <ListItemText primary={t(link.title)}/>
                                                <KeyboardArrowDownIcon sx={{color: '#FF8A65'}}/>
                                            </ListItem>
                                            <Collapse in={Boolean(featuresAnchorEl)}>
                                                <List>
                                                    {link.subNavLinks.map((subLink) => (
                                                        <ListItem
                                                            component={RouterLink}
                                                            key={subLink.name}
                                                            to={subLink.to || ''}
                                                            onClick={() => {
                                                                handleFeaturesMenuClose();
                                                                setMobileMenuOpen(false);
                                                            }}
                                                            sx={{
                                                                pl: 4,
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(255, 138, 101, 0.04)',
                                                                    '& .MuiListItemText-root': {color: '#FF8A65'}
                                                                },
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <ListItemText primary={t(subLink.title)}/>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </Box>
                                    ) : (
                                        <ListItem
                                            component={RouterLink}
                                            key={link.title}
                                            to={link.to || ''}
                                            onClick={() => setMobileMenuOpen(false)}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'rgba(255, 138, 101, 0.04)',
                                                    '& .MuiListItemText-root': {color: '#FF8A65'}
                                                }
                                            }}
                                        >
                                            <ListItemText primary={t(link.title)}/>
                                        </ListItem>
                                    )
                                ))}
                            </List>
                        </Drawer>
                    </>
                )}

                <RouterLink to="/">
                    <Box
                        component="img"
                        src={getImage('logo')}
                        alt="Logo"
                        sx={{
                            height: 36,
                            width: 202,
                            mt: 0.5, // Move logo down by 4px
                            display: 'block'
                        }}
                    />
                </RouterLink>

                <Box sx={{flexGrow: 1}}/>

                {isLargeScreen && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 12,
                        '& .MuiButton-root': {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            textTransform: 'uppercase',
                            mx: 1,
                            px: 1,
                            color: '#fff',
                            '&:hover': {
                                color: '#04BF7B',
                                backgroundColor: 'rgba(4, 191, 123, 0.08)'
                            },
                            '&.active': {
                                color: '#04BF7B'
                            }
                        },
                        justifyContent: 'center'
                    }}>
                        {navLinks.map((link) => (
                            link.subNavLinks ? (
                                <Box key={link.title}>
                                    <Button
                                        onClick={handleFeaturesMenuOpen}
                                        className={link.subNavLinks?.some(subLink => location.pathname === (subLink.to || '')) ? 'active' : ''}
                                        endIcon={<KeyboardArrowDownIcon fontSize="small"
                                                                        sx={{color: (featuresAnchorEl || link.subNavLinks?.some(subLink => location.pathname === (subLink.to || ''))) ? '#04BF7B' : 'inherit'}}/>}
                                        sx={{
                                            textTransform: 'none',
                                            ml: 3,
                                            color: '#fff',
                                            fontSize: '0.875rem',
                                            fontWeight: 400
                                        }}
                                    >
                                        {t(link.title)}
                                    </Button>
                                    <Menu
                                        anchorEl={featuresAnchorEl}
                                        open={Boolean(featuresAnchorEl)}
                                        onClose={handleFeaturesMenuClose}
                                        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                                        transformOrigin={{vertical: 'top', horizontal: 'left'}}
                                        PaperProps={{
                                            sx: {
                                                backgroundColor: '#2A3447',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 400,
                                                    textTransform: 'uppercase',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        color: '#04BF7B',
                                                        backgroundColor: 'rgba(4, 191, 123, 0.08)'
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        {link.subNavLinks.map((subLink) => (
                                            <MenuItem
                                                key={subLink.name}
                                                component={RouterLink}
                                                to={subLink.to || ''}
                                                onClick={handleFeaturesMenuClose}
                                            >
                                                {t(subLink.title)}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </Box>
                            ) : (
                                <Button
                                    key={link.title}
                                    component={RouterLink}
                                    to={link.to || ''}
                                    className={location.pathname === (link.to || '') ? 'active' : ''}
                                    sx={{
                                        textTransform: 'none',
                                        mx: 1,
                                        px: 1,
                                        color: '#fff',
                                        fontSize: '0.875rem',
                                        fontWeight: 400
                                    }}
                                >
                                    {t(link.title)}
                                </Button>
                            )
                        ))}
                    </Box>
                )}

                <Box sx={{flexGrow: 1}}/>

                {isMediumScreen && (
                    <>
                        <IconButton
                            onClick={() => dispatch(toggleTheme())}
                            sx={{color: '#fff'}}
                        >
                            {darkMode ? <LightModeOutlinedIcon sx={{fontSize: '1.25rem'}}/> :
                                <DarkModeOutlinedIcon sx={{fontSize: '1.25rem'}}/>}
                        </IconButton>

                        <Button
                            onClick={handleLanguageMenuOpen}
                            endIcon={<KeyboardArrowDownIcon fontSize="small"/>}
                            sx={{
                                textTransform: 'none',
                                color: '#fff',
                                px: 0
                            }}
                        >
                            <TranslateOutlinedIcon sx={{fontSize: '1.25rem'}}/>
                        </Button>
                        <Menu
                            anchorEl={languageAnchorEl}
                            open={Boolean(languageAnchorEl)}
                            onClose={handleLanguageMenuClose}
                        >
                            <MenuItem disabled>
                                {t('langs.translations')}
                            </MenuItem>
                            {[
                                {code: 'en', name: 'English'},
                                {code: 'ru', name: 'Русский'},
                                {code: 'pl', name: 'Polski'},
                                {code: 'ua', name: 'Українська'}
                            ].map(({code, name}) => (
                                <MenuItem
                                    key={code}
                                    onClick={() => changeLanguage(code)}
                                    selected={i18n.language === code}
                                >
                                    {name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}

                <Button
                    href={`${API_BASE_URL}/login`}
                    variant="outlined"
                    sx={{
                        textTransform: 'uppercase',
                        fontSize: '0.813rem',
                        fontWeight: 400,
                        color: '#F5A623 !important',
                        borderColor: '#F5A623',
                        width: '85px',
                        height: '36px',
                        '&:hover': {
                            borderColor: '#F5A623',
                            bgcolor: 'rgba(245, 166, 35, 0.08)'
                        }
                    }}
                >
                    {t('navigation.login')}
                </Button>

                {isMediumScreen && (
                    <Button
                        component={RouterLink}
                        to="/user/register"
                        variant="contained"
                        sx={{
                            ml: 2,
                            textTransform: 'uppercase',
                            fontSize: '0.813rem',
                            fontWeight: 400,
                            bgcolor: '#04BF7B',
                            color: '#fff',
                            width: '155px',
                            height: '36px',
                            '&:hover': {
                                bgcolor: '#03a66a'
                            }
                        }}
                    >
                        {t('navigation.start-testing')}
                    </Button>
                )}
            </Toolbar>
        </AppBar>
    );
}
